import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { Plus } from "@phosphor-icons/react";

import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import AdminTable from "../../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../../components/adminDashboard/AdminTable/AdminTableHeader";
import AdminTablePagination from "../../../components/adminDashboard/AdminTable/AdminTablePagination";
import AdminDegreeRosterManagementTable from "../../../components/adminDashboard/AdminDegreeRosterManagementTable";
import InviteStudentModalContainer from "../../courseSettings/RosterManagementContainer/InviteStudentModalContainer";
import {
  useGetStudentsByDegreeIdQuery,
  useInviteUsersToEnrollInDegreeMutation,
  useRemoveStudentsFromDegreeMutation,
} from "../../../graphql/generated/graphql";
import usePagination from "../../../hooks/usePagination";
import useSortingDropdown from "../../../hooks/useSortingDropdown";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";

const SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
];

interface AdminDegreeRosterManagementContainerProps {
  degreeId: string;
  refetchDegreeData: () => void;
}

const AdminDegreeRosterManagementContainer: React.FC<AdminDegreeRosterManagementContainerProps> = ({
  degreeId,
  refetchDegreeData,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalState, setModalState] = useState({
    visible: false,
    title: "",
    message: "",
    color: "#111827",
  });

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });

  const {
    data: getStudentsByDegreeIdData,
    loading,
    error,
    refetch: getStudentsByDegreeIdRefetch,
  } = useGetStudentsByDegreeIdQuery({
    variables: {
      degreeId: degreeId,
      searchQuery,
      page: page,
      pageSize: pageSize,
      sortBy,
    },
    skip: !degreeId,
  });

  const students = getStudentsByDegreeIdData?.getStudentsByDegreeId.data || [];
  const totalCount = getStudentsByDegreeIdData?.getStudentsByDegreeId.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const [inviteUsersToEnrollInDegree] = useInviteUsersToEnrollInDegreeMutation();
  const [removeStudentsFromDegree] = useRemoveStudentsFromDegreeMutation();

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    getStudentsByDegreeIdRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  const handleInviteStudents = async (studentsToInvite: { name: string; email: string }[]) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUsersToEnrollInDegree({
        variables: {
          input: {
            degreeId: degreeId,
            users: studentsToInvite,
          },
        },
      });

      if (result.data?.inviteUsersToEnrollInDegree.success) {
        getStudentsByDegreeIdRefetch();
        refetchDegreeData();
        setIsInviteModalVisible(false);
        setIsSubmitting(false);
        return true;
      }
      setIsSubmitting(false);
      return false;
    } catch (error) {
      console.error("Error inviting students:", error);
      setIsSubmitting(false);
      return false;
    }
  };

  const handleResendInvite = async (studentName: string, studentEmail: string) => {
    try {
      const result = await inviteUsersToEnrollInDegree({
        variables: {
          input: {
            degreeId: degreeId,
            users: students,
          },
        },
      });

      if (result.data?.inviteUsersToEnrollInDegree.success) {
        setModalState({
          visible: true,
          title: "Message Sent",
          message: "The invitation has been resent successfully.",
          color: "#111827",
        });
        setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
      }
    } catch (error) {
      console.error("Error resending invite:", error);
      setModalState({
        visible: true,
        title: "Error",
        message: "Failed to resend the invitation. Please try again.",
        color: "#DC2626",
      });
      setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
    }
  };

  const handleRemoveStudents = useCallback(
    async (studentId: string) => {
      try {
        await removeStudentsFromDegree({
          variables: {
            input: {
              degreeId: degreeId,
              studentIds: [studentId],
            },
          },
        });
        getStudentsByDegreeIdRefetch();
        refetchDegreeData();
        setModalState({
          visible: true,
          title: "Student Removed",
          message: "The student has been successfully removed from the Degree Program.",
          color: "#111827",
        });
        setTimeout(() => setModalState((prev) => ({ ...prev, visible: false })), 2500);
      } catch (error) {
        console.error("Error removing students:", error);
      }
    },
    [degreeId, removeStudentsFromDegree]
  );

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Error loading students" size="m" useTranslationText={false} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <CustomText
            text={`Enrolled Students (${totalCount})`}
            size="l"
            weight="bold"
            style={styles.title}
            useTranslationText={false}
          />
        </View>
        <View style={styles.headerRight}>
          <CustomButton
            text="Add Student"
            styleType="primary"
            size="m"
            leftIcon={<Plus size={24} color="#FFFFFF" />}
            onPress={() => setIsInviteModalVisible(true)}
            useTranslationText={false}
            textStyle={{ color: "#FFFFFF", fontWeight: "700", fontSize: 14 }}
            style={styles.addStudentButton}
          />
        </View>
      </View>

      <AdminTable>
        <AdminTableHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDropdownOpen={sortDropdownOpen}
          setSortDropdownOpen={setSortDropdownOpen}
          sortItems={SORT_ITEMS}
        />

        <AdminDegreeRosterManagementTable
          students={students}
          onResendInvite={handleResendInvite}
          handleRemoveStudents={handleRemoveStudents}
        />

        {totalCount > 0 && (
          <AdminTablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
          />
        )}
      </AdminTable>

      <InviteStudentModalContainer
        visible={isInviteModalVisible}
        onClose={() => setIsInviteModalVisible(false)}
        onSubmit={handleInviteStudents}
        isSubmitting={isSubmitting}
      />
      <CustomModal
        visible={modalState.visible}
        onRequestClose={() => setModalState((prev) => ({ ...prev, visible: false }))}
        showCloseButton
        title={modalState.title}>
        <CustomText
          text={modalState.message}
          size="m"
          style={{ textAlign: "center", marginVertical: 16, color: modalState.color }}
          useTranslationText={false}
        />
      </CustomModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 32,
    backgroundColor: "#FFFFFF",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flexDirection: "row",
    gap: 12,
  },
  title: {
    color: "#111827",
  },
  addStudentButton: {
    borderRadius: 8,
  },
});

export default AdminDegreeRosterManagementContainer;
